// A PDF shown inside the app, so looking at one never means leaving it.
//
// `PdfFrame` is the split DocumentViewer made first: the browser's own viewer
// in an <object> where it says it has one (zoom, search and print for free),
// and pdf.js drawing the pages where it does not, which is every phone (iOS
// Safari paints an embedded PDF as a frozen picture of page one; Android
// Chrome will not embed at all). `PdfViewer` puts a frame in a dialog with
// Download and Close, for a PDF that is not an attachment (a timesheet's).
import { Suspense, lazy } from 'react';
import { DownloadLink } from './DownloadLink';

const PdfPages = lazy(() => import('./PdfPages'));

// True only when the browser states it renders embedded PDFs. Read per open,
// not at module load, so it can be overridden when verifying.
export const nativePdfViewer = () => typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === true;

export function PdfFrame({ src, downloadHref, label }: { src: string; downloadHref: string; label: string }) {
  if (nativePdfViewer()) {
    return (
      <object className="viewer-pdf" data={src} type="application/pdf" aria-label={label}>
        {/* Shown when the browser lied about having a PDF viewer. */}
        <p className="muted">
          Your browser cannot show this PDF here. <DownloadLink href={downloadHref}>Download it instead</DownloadLink>.
        </p>
      </object>
    );
  }
  return (
    <Suspense fallback={<p className="muted">Loading the PDF…</p>}>
      <PdfPages src={src} downloadHref={downloadHref} />
    </Suspense>
  );
}

export function PdfViewer({
  title,
  src,
  downloadHref,
  onClose,
}: {
  title: string;
  src: string;
  downloadHref: string;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal viewer"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="row mb-5">
          <h1 className="!mb-0">{title}</h1>
          <span className="spacer" />
          <DownloadLink href={downloadHref} className="btn-secondary">
            Download
          </DownloadLink>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
        <PdfFrame src={src} downloadHref={downloadHref} label={title} />
      </div>
    </div>
  );
}
